import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Download, FileText, Send } from "lucide-react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import Footer from "./Footer";
import { apiGet, apiPost } from "../services/api";

const DEFAULT_MAX_LEAVE_DAYS = 5;
const DEFAULT_MESS_DEDUCTION_MIN_DAYS = 3;

function getInclusiveLeaveDays(fromDate, toDate) {
  if (!fromDate || !toDate) return 0;
  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }
  return Math.floor((end - start) / 86400000) + 1;
}

function addDays(dateString, daysToAdd) {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + daysToAdd);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getParentContact(profile = {}) {
  return profile.Fmobile || profile.MMobile || profile.mother_mobile || "";
}

function isBlockingLeaveStatus(status = "") {
  const normalized = status.toLowerCase();
  return normalized !== "rejected" && normalized !== "cancelled" && normalized !== "canceled";
}

function datesOverlap(firstFrom, firstTo, secondFrom, secondTo) {
  return firstFrom <= secondTo && firstTo >= secondFrom;
}

function getLeaveApplicationsFromResponse(response = {}) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.applications)) return response.applications;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.leaves)) return response.leaves;
  if (Array.isArray(response.leaveApplications)) return response.leaveApplications;
  if (response.application) return [response.application];
  return [];
}

function normalizeLeaveApplication(application = {}, fallback = {}) {
  const fromDate = application.fromDate || application.from_date || application.from || fallback.fromDate || "";
  const toDate = application.toDate || application.to_date || application.to || fallback.toDate || "";
  const totalDays =
    application.totalDays ||
    application.total_days ||
    application.noOfDays ||
    application.no_of_days ||
    getInclusiveLeaveDays(fromDate, toDate);

  return {
    ...application,
    id:
      application.id ||
      application.applicationId ||
      application.application_id ||
      application.leave_id ||
      application.onlineApplicationNumber ||
      application.online_application_number ||
      fallback.id,
    fromDate,
    toDate,
    reason: application.reason || application.leave_reason || fallback.reason || "",
    destination:
      application.destination ||
      application.stayingOutAddress ||
      application.staying_out_address ||
      application.address ||
      fallback.destination ||
      "",
    guardianContact:
      application.guardianContact ||
      application.guardian_contact ||
      application.parent_mobile ||
      application.parents_mobile ||
      fallback.guardianContact ||
      "",
    department: application.department || fallback.department || "",
    course: application.course || application.programme || application.program || fallback.course || "",
    hostelName: application.hostelName || application.hostel_name || fallback.hostelName || "",
    roomNumber: application.roomNumber || application.room_number || fallback.roomNumber || "",
    totalDays,
    messDeductionApplicable:
      application.messDeductionApplicable ??
      application.mess_deduction_applicable ??
      application.messDeductionNeeded ??
      application.mess_deduction_needed ??
      fallback.messDeductionApplicable ??
      false,
    status: application.status || fallback.status || "Submitted",
    appliedAt:
      application.appliedAt ||
      application.applied_at ||
      application.created_at ||
      application.createdAt ||
      application.date ||
      fallback.appliedAt ||
      "",
  };
}

function getApplicationSortTime(application = {}) {
  const dateValue = application.appliedAt || application.fromDate || application.toDate || "";
  const parsedDate = new Date(dateValue).getTime();
  if (!Number.isNaN(parsedDate)) return parsedDate;

  const numericId = Number(String(application.id || "").replace(/\D/g, ""));
  return Number.isNaN(numericId) ? 0 : numericId;
}

function getNormalizedGender(gender = "") {
  const normalized = String(gender).trim().toLowerCase();
  if (normalized === "f" || normalized === "female" || normalized === "girl" || normalized === "girls") {
    return "female";
  }
  if (normalized === "m" || normalized === "male" || normalized === "boy" || normalized === "boys") {
    return "male";
  }
  return normalized;
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function findFirstNumberByKeys(source, keys) {
  if (!source || typeof source !== "object") return null;

  for (const key of keys) {
    const value = source[key];
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }

  for (const value of Object.values(source)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nestedValue = findFirstNumberByKeys(value, keys);
      if (nestedValue) return nestedValue;
    }
  }

  return null;
}

function getConfiguredLeaveDays(response = {}, gender = "") {
  const normalizedGender = getNormalizedGender(gender);
  const genderLimits = response.genderLimits || response.gender_limits || response.leaveLimits || response.leave_limits || {};
  const genderLimit =
    normalizedGender === "female"
      ? pickFirstNumber(
          response.girlsMaxLeaveDays,
          response.girls_max_leave_days,
          response.femaleMaxLeaveDays,
          response.female_max_leave_days,
          genderLimits.female,
          genderLimits.girls,
          genderLimits.F
        )
      : normalizedGender === "male"
      ? pickFirstNumber(
          response.boysMaxLeaveDays,
          response.boys_max_leave_days,
          response.maleMaxLeaveDays,
          response.male_max_leave_days,
          genderLimits.male,
          genderLimits.boys,
          genderLimits.M
        )
      : null;

  return (
    genderLimit ||
    pickFirstNumber(response.maxLeaveDays, response.max_leave_days, response.maxDays, response.max_days) ||
    DEFAULT_MAX_LEAVE_DAYS
  );
}

function getConfiguredMessDeductionMinDays(response = {}) {
  return (
    findFirstNumberByKeys(response, [
      "messDeductionMinDays",
      "mess_deduction_min_days",
      "messRebateMinDays",
      "mess_rebate_min_days",
      "messDeductionAfterDays",
      "mess_deduction_after_days",
      "messRebateAfterDays",
      "mess_rebate_after_days",
      "minMessDeductionDays",
      "min_mess_deduction_days",
      "minMessRebateDays",
      "min_mess_rebate_days",
    ]) || DEFAULT_MESS_DEDUCTION_MIN_DAYS
  );
}

export default function LeaveApplication({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [leaveDataWarning, setLeaveDataWarning] = useState("");
  const [applications, setApplications] = useState([]);
  const [leaveBlock, setLeaveBlock] = useState({ isBlocked: false });
  const [hostelAllocation, setHostelAllocation] = useState(null);
  const [maxLeaveDays, setMaxLeaveDays] = useState(DEFAULT_MAX_LEAVE_DAYS);
  const [messDeductionMinDays, setMessDeductionMinDays] = useState(DEFAULT_MESS_DEDUCTION_MIN_DAYS);
  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    reason: "",
    destination: "",
    guardianContact: "",
    messDeductionNeeded: false,
  });
  const [submittedApplication, setSubmittedApplication] = useState(null);

  // Compute today's date in YYYY-MM-DD format for input min attributes
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  useEffect(() => {
    async function fetchPageData() {
      try {
        setLoading(true);
        setError("");
        setLeaveDataWarning("");
        const studentRes = await apiGet("student");

        if (studentRes.error) {
          setError(studentRes.error);
          return;
        }

        setStudentData(studentRes);
        setFormData((prev) => ({
          ...prev,
          guardianContact: prev.guardianContact || getParentContact(studentRes.profile),
        }));

        setHostelAllocation(studentRes.allocation || null);

        const leaveRes = await apiGet("leave_applications");
        if (leaveRes.error) {
          setLeaveDataWarning(leaveRes.error);
          return;
        }

        const allocation = studentRes.allocation || {};
        const applicationFallback = {
          department: studentRes.profile?.department || "",
          course: studentRes.profile?.ProgrammeName || "",
          hostelName: allocation.hostelName || allocation.hostel_name || "",
          roomNumber: allocation.roomNumber || allocation.room_number || "",
          guardianContact: getParentContact(studentRes.profile),
        };
        const fetchedApplications = getLeaveApplicationsFromResponse(leaveRes)
          .map((application) => normalizeLeaveApplication(application, applicationFallback))
          .sort((a, b) => getApplicationSortTime(b) - getApplicationSortTime(a));
        const activeApplication =
          fetchedApplications.find((application) => isBlockingLeaveStatus(application.status)) || null;

        setApplications(fetchedApplications);
        setSubmittedApplication(activeApplication);
        setLeaveBlock(leaveRes.leaveBlock || { isBlocked: Boolean(leaveRes.isLeaveBlocked) });
        if (activeApplication) {
          setFormData((prev) => ({
            ...prev,
            fromDate: activeApplication.fromDate || prev.fromDate,
            toDate: activeApplication.toDate || prev.toDate,
            reason: activeApplication.reason || prev.reason,
            destination: activeApplication.destination || prev.destination,
            guardianContact: activeApplication.guardianContact || prev.guardianContact,
            messDeductionNeeded: Boolean(activeApplication.messDeductionApplicable),
          }));
        }
        const studentGender = studentRes.profile?.gender || studentRes.student?.gender || "";
        setMaxLeaveDays(getConfiguredLeaveDays(leaveRes, studentGender));
        setMessDeductionMinDays(getConfiguredMessDeductionMinDays(leaveRes));
      } catch (err) {
        setError("Unable to fetch leave application data.");
      } finally {
        setLoading(false);
      }
    }

    fetchPageData();
  }, []);

  const gender = studentData?.profile?.gender || studentData?.student?.gender || "";
  const normalizedGender = getNormalizedGender(gender);
  const studentTypeLabel =
    normalizedGender === "female" ? "Girls" : normalizedGender === "male" ? "Boys" : "Student";
  const department = studentData?.profile?.department || "";
  const course = studentData?.profile?.ProgrammeName || "";
  const hostelName =
    hostelAllocation?.hostelName ||
    hostelAllocation?.hostel_name ||
    studentData?.allocation?.hostel_name ||
    "";
  const roomNumber =
    hostelAllocation?.roomNumber ||
    hostelAllocation?.room_number ||
    studentData?.allocation?.room_number ||
    "";
  const parentContact = getParentContact(studentData?.profile);

  // Check if selected fromDate is in the past
  const isPastDate = useMemo(() => {
    if (!formData.fromDate) return false;
    return formData.fromDate < todayStr;
  }, [formData.fromDate, todayStr]);

  const totalDays = useMemo(
    () => getInclusiveLeaveDays(formData.fromDate, formData.toDate),
    [formData.fromDate, formData.toDate]
  );
  const maxLeaveToDate = useMemo(() => {
    if (!formData.fromDate || !maxLeaveDays) return "";
    return addDays(formData.fromDate, maxLeaveDays - 1);
  }, [formData.fromDate, maxLeaveDays]);

  const isOverLimit = Boolean(maxLeaveDays) && totalDays > maxLeaveDays;
  const hasInvalidRange = formData.fromDate && formData.toDate && totalDays === 0;
  const canApplyMessDeduction = totalDays > messDeductionMinDays;
  const overlappingApplication = useMemo(() => {
    if (!formData.fromDate || !formData.toDate || !totalDays) return null;

    return (
      applications.find(
        (application) =>
          isBlockingLeaveStatus(application.status) &&
          datesOverlap(
            application.fromDate,
            application.toDate,
            formData.fromDate,
            formData.toDate
          )
      ) || null
    );
  }, [applications, formData.fromDate, formData.toDate, totalDays]);
  const leaveAlreadyApplied = Boolean(
    submittedApplication || applications.some((application) => isBlockingLeaveStatus(application.status))
  );
  const isLeaveBlocked = Boolean(leaveBlock?.isBlocked || leaveBlock?.is_blocked);
  const cannotEditLeaveForm = leaveAlreadyApplied || isLeaveBlocked;
  const isOverlappingLeave = Boolean(overlappingApplication);

  const handleInputChange = (field, value) => {
    if (cannotEditLeaveForm) return;

    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "fromDate" && next.toDate) {
        const maxToDate = maxLeaveDays ? addDays(value, maxLeaveDays - 1) : "";
        if (next.toDate < value || (maxToDate && next.toDate > maxToDate)) {
          next.toDate = "";
        }
      }
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent submission if the date is in the past, zero days, or over limit
    if (cannotEditLeaveForm || !totalDays || isOverLimit || isPastDate || isOverlappingLeave) return;

    try {
      setSubmitting(true);
      setError("");
      const res = await apiPost("leave_applications", {
        ...formData,
        department,
        course,
        messDeductionNeeded: formData.messDeductionNeeded && canApplyMessDeduction,
      });

      if (res.error) {
        setError(res.error);
        if (res.isLeaveBlocked || res.leaveBlock?.isBlocked) {
          setLeaveBlock(res.leaveBlock || { isBlocked: true });
        }
        return;
      }

      const application = normalizeLeaveApplication(res.application, {
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        reason: formData.reason,
        destination: formData.destination,
        guardianContact: formData.guardianContact || parentContact,
        department,
        course,
        hostelName,
        roomNumber,
        totalDays,
        messDeductionApplicable: formData.messDeductionNeeded && canApplyMessDeduction,
        status: "Submitted",
        appliedAt: new Date().toLocaleDateString(),
      });

      setApplications((prev) => [application, ...prev]);
      setSubmittedApplication(application);
    } catch (err) {
      setError("Unable to submit leave application.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadLeaveForm = async () => {
    if (!submittedApplication) return;

    try {
      setDownloading(true);
      setError("");
      const jsPDF = (await import("jspdf")).default;
      const html2canvas = (await import("html2canvas")).default;
      const element = document.getElementById("leave-form-download");

      if (!element) {
        throw new Error("Leave form not found");
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: 794,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        letterRendering: true,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, 210, 297);
      pdf.save(`Hostel_Leave_Form_${submittedApplication.id || studentData?.student?.roll_number || "Student"}.pdf`);
    } catch (err) {
      setError("Unable to download leave form. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} onLogout={onLogout} />

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col md:ml-64">
        <Topbar setSidebarOpen={setSidebarOpen} enrollment={studentData?.student?.enrollment_id} />

        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold text-blue-800">Apply Leave</h2>
                <p className="text-gray-500 mt-1">
                  Submit hostel leave dates and track your application status.
                </p>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                {studentTypeLabel} maximum leave: <span className="font-semibold">{maxLeaveDays} days</span>
              </div>
            </div>
          </div>

          {loading && <p className="text-gray-500">Loading...</p>}
          {error && <p className="text-red-600">{error}</p>}
          {leaveDataWarning && !error && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              {leaveDataWarning}
            </p>
          )}

          {!loading && !error && (
            <>
              <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6 space-y-5">
                {leaveAlreadyApplied && (
                  <p className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
                    Leave application submitted. You can download the generated form below.
                  </p>
                )}
                {isLeaveBlocked && (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
                     Hostel administration block the student to apply for the leave. Please contact the administration.
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ReadOnlyField label="Student Name" value={studentData?.student?.name} />
                  <ReadOnlyField label="Enrollment No" value={studentData?.student?.enrollment_id} />
                  <ReadOnlyField label="Gender" value={gender || "N/A"} />
                  <ReadOnlyField label="Department" value={department} />
                  <ReadOnlyField label="Course" value={course} />
                  <ReadOnlyField label="Hostel" value={hostelName} />
                  <ReadOnlyField label="Room No" value={roomNumber} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Leave From">
                    <input
                      type="date"
                      required
                      min={todayStr}
                      value={formData.fromDate}
                      disabled={cannotEditLeaveForm}
                      onChange={(e) => handleInputChange("fromDate", e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </FormField>
                  <FormField label="Leave To">
                    <input
                      type="date"
                      required
                      min={formData.fromDate || todayStr}
                      max={maxLeaveToDate || undefined}
                      value={formData.toDate}
                      disabled={cannotEditLeaveForm || !formData.fromDate}
                      onChange={(e) => handleInputChange("toDate", e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                    {formData.fromDate && maxLeaveToDate && (
                      <span className="mt-1 block text-xs text-gray-500">
                        You can select up to {maxLeaveToDate} only. The {maxLeaveDays}-day limit starts from the leave from date.
                      </span>
                    )}
                  </FormField>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <CalendarDays className="h-4 w-4 text-blue-600" />
                      Total Days
                    </div>
                    <p className={`mt-2 text-2xl font-semibold ${isOverLimit || isPastDate ? "text-red-600" : "text-gray-900"}`}>
                      {totalDays || 0}
                    </p>
                  </div>
                  <FormField label="Destination">
                    <input
                      type="text"
                      required
                      value={formData.destination}
                      disabled={cannotEditLeaveForm}
                      onChange={(e) => handleInputChange("destination", e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
                      placeholder="Where are you going?"
                    />
                  </FormField>
                  <FormField label="Parent / Guardian Contact">
                    <input
                      type="tel"
                      required
                      value={formData.guardianContact}
                      disabled={cannotEditLeaveForm}
                      onChange={(e) => handleInputChange("guardianContact", e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
                      placeholder="Contact number"
                    />
                  </FormField>
                </div>

                <FormField label="Reason for Leave">
                  <textarea
                    required
                    rows="4"
                    value={formData.reason}
                    disabled={cannotEditLeaveForm}
                    onChange={(e) => handleInputChange("reason", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
                    placeholder="Enter leave reason"
                  />
                </FormField>

                <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.messDeductionNeeded && canApplyMessDeduction}
                    disabled={cannotEditLeaveForm || !canApplyMessDeduction}
                    onChange={(e) => handleInputChange("messDeductionNeeded", e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    Mess deduction needed
                    <span className="block text-gray-500">
                      Available only when leave is more than {messDeductionMinDays} days.
                    </span>
                  </span>
                </label>

                {isPastDate && (
                  <p className="text-sm font-medium text-red-600">
                    Leave cannot be applied for past dates.
                  </p>
                )}
                {hasInvalidRange && !isPastDate && (
                  <p className="text-sm font-medium text-red-600">Please select a valid date range.</p>
                )}
                {isOverLimit && (
                  <p className="text-sm font-medium text-red-600">
                    Selected leave exceeds the configured limit of {maxLeaveDays} days for this student.
                  </p>
                )}
                {isOverlappingLeave && (
                  <p className="text-sm font-medium text-red-600">
                    You have already applied for leave during this period ({overlappingApplication.fromDate} to {overlappingApplication.toDate}).
                  </p>
                )}

                <button
                  type="submit"
                  disabled={cannotEditLeaveForm || !totalDays || isOverLimit || isPastDate || isOverlappingLeave || submitting}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  <Send className="h-4 w-4" />
                  {leaveAlreadyApplied
                    ? "Leave Already Applied"
                    : isLeaveBlocked
                    ? "Leave Application Blocked"
                    : isOverlappingLeave
                    ? "Already Applied For This Period"
                    : submitting
                    ? "Submitting..."
                    : "Submit Leave Application"}
                </button>
              </form>

              {submittedApplication && (
                <div className="bg-white rounded-xl shadow p-6 space-y-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Download Leave Form</h3>
                      <p className="text-sm text-gray-500">
                        Your leave application was submitted. Download this one-page form for signatures.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadLeaveForm}
                      disabled={downloading}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      <Download className="h-4 w-4" />
                      {downloading ? "Downloading..." : "Download Form"}
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-100 p-4">
                    <LeaveApplicationForm
                      application={submittedApplication}
                      studentData={studentData}
                    />
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Leave Application Status</h3>
                </div>
                {applications.length === 0 ? (
                  <p className="text-gray-500">No leave applications submitted yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left">Application ID</th>
                          <th className="px-4 py-2 text-left">Applied On</th>
                          <th className="px-4 py-2 text-left">Department</th>
                          <th className="px-4 py-2 text-left">Course</th>
                          <th className="px-4 py-2 text-left">Hostel</th>
                          <th className="px-4 py-2 text-left">Room</th>
                          <th className="px-4 py-2 text-left">From</th>
                          <th className="px-4 py-2 text-left">To</th>
                          <th className="px-4 py-2 text-left">Days</th>
                          <th className="px-4 py-2 text-left">Mess Deduction</th>
                          <th className="px-4 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.map((application) => (
                          <tr key={application.id} className="border-t">
                            <td className="px-4 py-2 font-mono">{application.id}</td>
                            <td className="px-4 py-2">{application.appliedAt}</td>
                            <td className="px-4 py-2">{application.department || "N/A"}</td>
                            <td className="px-4 py-2">{application.course || "N/A"}</td>
                            <td className="px-4 py-2">{application.hostelName || "N/A"}</td>
                            <td className="px-4 py-2">{application.roomNumber || "N/A"}</td>
                            <td className="px-4 py-2">{application.fromDate}</td>
                            <td className="px-4 py-2">{application.toDate}</td>
                            <td className="px-4 py-2">{application.totalDays}</td>
                            <td className="px-4 py-2">
                              {application.messDeductionApplicable ? "Applicable" : "No"}
                            </td>
                            <td className="px-4 py-2">{application.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </main>

        <Footer />
      </div>
    </div>
  );
}

function LeaveApplicationForm({ application, studentData }) {
  const student = studentData?.student || {};
  const profile = studentData?.profile || {};
  const studentName = student.name || "";
  const programme = [application.course, application.department].filter(Boolean).join(" / ");
  const hostelRoom = [application.hostelName, application.roomNumber && `Room ${application.roomNumber}`]
    .filter(Boolean)
    .join(" & ");
  const permanentAddress = profile.PAddress || profile.CAddress || "";
  const parentMobile = application.guardianContact || getParentContact(profile);
  const messFrom = application.messDeductionApplicable ? application.fromDate : "";
  const messTo = application.messDeductionApplicable ? application.toDate : "";
  const messDays = application.messDeductionApplicable ? application.totalDays : "";

  return (
    <div
      id="leave-form-download"
      className="mx-auto h-[1123px] w-[794px] overflow-hidden bg-white px-10 py-6 text-[14px] leading-tight text-black shadow print:shadow-none"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      <div className="text-center font-bold uppercase leading-tight tracking-normal">
        
        <p>Gautam Buddha University, Greater Noida</p>
        <p>Hostel Leave and Mess Rebate Form</p>
        <div className="mx-auto mt-1.5 h-px w-48 bg-black" />
      </div>

      <div className="mt-6 space-y-3">
        <FormLine label="Hostel" value={application.hostelName} centered />
        <FormLine label="Name of Student" value={studentName} />
        <FormLine
          label="Roll No./Semester/Programme"
          value={[student.roll_number || student.enrollment_id, programme].filter(Boolean).join(" / ")}
        />
        <FormLine label="Room No." value={application.roomNumber} />
        <FormLine label="Permanent Home Address" value={permanentAddress} />
        <FormLine label="" value="" indent />
        <FormLine label="Mobile No. (Parents)" value={parentMobile} />

        <div className="grid grid-cols-[155px_50px_1fr_28px_1fr_90px_1fr] items-end gap-2">
          <span className="font-bold">Hostel Leave Period:</span>
          <span className="font-bold">From</span>
          <DottedValue value={application.fromDate} />
          <span className="font-bold">To</span>
          <DottedValue value={application.toDate} />
          <span className="font-bold">No. of days</span>
          <DottedValue value={application.totalDays} />
        </div>

        <div className="grid grid-cols-[210px_50px_1fr_28px_1fr_42px_1fr] items-end gap-2">
          <span className="font-bold">Mess Rebate (Minimum 05 Days):</span>
          <span className="font-bold">From</span>
          <DottedValue value={messFrom} />
          <span className="font-bold">To</span>
          <DottedValue value={messTo} />
          <span className="font-bold">Days</span>
          <DottedValue value={messDays} />
        </div>

        <p className="-mt-2 text-[13px]">
          (Applicable only during official University holidays / student field work / summer & winter break)
        </p>

        <FormLine label="Reason(s) for Staying Out" value={application.reason} />
        <FormLine label="Staying Out Address and Contact No." value={application.destination} />
        <div className="border-b border-dotted border-gray-500 pt-2" />
        <FormLine label="Online Application Number" value={application.id || "Pending"} />
      </div>

      <p className="mt-6 text-justify">
        I hereby declare that I have informed my parents about my hostel leaving plan and have taken
        their permission before leaving the hostel. I have also filled in the online leave form, and the
        entries made here are same as those in the online leave form. In case of cancellation of my
        programme, it is my responsibility to inform my parents and the hostel authorities in time. I
        understand that any breach of this undertaking may lead to disciplinary action.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-x-16 gap-y-5">
        <FormLine label="Name of Student" value={studentName} compact />
        <FormLine label="Signature" value="" compact />
        <FormLine label="Room No." value={application.roomNumber} compact />
        <FormLine label="Mobile No." value={student.mobile || parentMobile} compact />
      </div>

      <div className="mt-7 border border-black p-4">
        <p className="text-center font-bold uppercase">For Office Use Only</p>
        <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4">
          <FormLine label="Approved / Rejected" value="" compact />
          <FormLine label="Remarks" value="" compact />
          <FormLine label="Out Date & Time" value="" compact />
          <FormLine label="Return Date & Time" value="" compact />
        </div>
        <div className="mt-11 grid grid-cols-3 gap-8 text-center font-bold">
          <SignatureBox label="Caretaker" />
          <SignatureBox label="Warden" />
          <SignatureBox label="Chief Warden" />
        </div>
      </div>
    </div>
  );
}

function FormLine({ label, value, centered = false, indent = false, compact = false }) {
  return (
    <div
      className={`grid items-end gap-2 ${centered ? "mx-auto w-[455px] grid-cols-[auto_1fr]" : "grid-cols-[auto_1fr]"} ${
        compact ? "text-[13px]" : ""
      }`}
    >
      {label && <span className="font-bold">{label}:</span>}
      {indent && <span />}
      <DottedValue value={value} />
    </div>
  );
}

function SignatureBox({ label }) {
  return (
    <div>
      <div className="border-t border-black pt-2">{label}</div>
    </div>
  );
}

function DottedValue({ value }) {
  return (
    <span className="min-h-[19px] border-b border-dotted border-gray-500 px-2 leading-4">
      {value || ""}
    </span>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800">
        {value || "N/A"}
      </div>
    </div>
  );
}
